import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, Phone, Trash2, TrendingUp, User } from 'lucide-react';
import { PARTIES_THEME, partyTypeBadgeClass, partyTypeLabel } from './partiesTheme';

const theme = PARTIES_THEME;

export default function PartyCard({ party, onSelect, onEdit, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const balance = Number(party.openingBalance) || 0;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200/90 shadow-sm mb-3 overflow-hidden">
      <button
        type="button"
        className="w-full p-4 text-left active:bg-slate-50/80 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex gap-3 items-start">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white text-sm font-bold ${theme.iconTile}`}
          >
            {(party.firmName || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="text-sm font-bold text-slate-900 truncate">{party.firmName}</h4>
                {party.personName && (
                  <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                    <User className="h-3 w-3 shrink-0" aria-hidden />
                    {party.personName}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span
                  className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ring-1 ${partyTypeBadgeClass(party.partyType)}`}
                >
                  {partyTypeLabel(party.partyType)}
                </span>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-slate-400" aria-hidden />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {party.contact && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" aria-hidden />
                  {party.contact}
                </span>
              )}
              <span
                className={`ml-auto text-sm font-bold tabular-nums ${
                  balance > 0 ? 'text-emerald-600' : balance < 0 ? 'text-red-600' : 'text-slate-400'
                }`}
              >
                {balance > 0 ? '+' : ''}
                {balance.toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/40">
          <dl className="grid grid-cols-2 gap-2 py-3 text-xs">
            <div className="bg-white p-2.5 rounded-lg border border-slate-100">
              <dt className="text-slate-500 font-medium">Email</dt>
              <dd className="text-slate-800 truncate mt-0.5">{party.email || '—'}</dd>
            </div>
            <div className="bg-white p-2.5 rounded-lg border border-slate-100">
              <dt className="text-slate-500 font-medium">WhatsApp</dt>
              <dd className="text-slate-800 mt-0.5">{party.whatsapp || '—'}</dd>
            </div>
            <div className="bg-white p-2.5 rounded-lg border border-slate-100">
              <dt className="text-slate-500 font-medium">GST type</dt>
              <dd className="text-slate-800 mt-0.5">{party.gstType || '—'}</dd>
            </div>
            <div className="bg-white p-2.5 rounded-lg border border-slate-100">
              <dt className="text-slate-500 font-medium">GSTIN</dt>
              <dd className="text-slate-800 truncate mt-0.5">{party.gstin || '—'}</dd>
            </div>
            {party.address && (
              <div className="col-span-2 bg-white p-2.5 rounded-lg border border-slate-100">
                <dt className="text-slate-500 font-medium">Address</dt>
                <dd className="text-slate-800 mt-0.5">{party.address}</dd>
              </div>
            )}
          </dl>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSelect(party)}
              className={`flex-1 inline-flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-white rounded-xl bg-gradient-to-r ${theme.btn} shadow-sm`}
            >
              <TrendingUp className="h-3.5 w-3.5" aria-hidden />
              Outstanding
            </button>
            <button
              type="button"
              onClick={() => onEdit(party)}
              className="flex-1 inline-flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-teal-800 bg-teal-50 rounded-xl hover:bg-teal-100"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(party.id)}
              className="inline-flex items-center justify-center p-2.5 text-red-700 bg-red-50 rounded-xl hover:bg-red-100"
              aria-label="Delete party"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
